import React from "react";
import { useSelector } from "react-redux";
import useDispatch from "@/Components/Utils/useDispatch";
import { DefaultErrorMessage } from "@/Contracts";
import ZMessage from "@molecules/ZMessage";
import DownloadDocumentModal from "@/Components/DocumentsPage/Components/DownloadDocumentModal";
import DocumentsActionCreators from "@/Actions/Documents";
import { DownloadDocumentProductListRequest } from "@/Contracts/Documents";
import { GlobalState } from "@/Reducers/interface";
import {
	downloadWonDocument as downloadDocumentUsingURL,
	downloadWonDocument
} from "@/Services/Documents";
import { DownloadHook } from "./interface";
import SettingsActionCreators from "@/Actions/Settings";

const useDownload = (): DownloadHook => {
	const { currentActiveDocument, downloadDocumentModalOpen } = useSelector(
		(state: GlobalState) => ({
			currentActiveDocument: state.documents.currentActiveDocument,
			downloadDocumentModalOpen:
				state.documents.downloadDocumentModalOpen
		})
	);

	const dispatch = useDispatch();

	const downloadDocument = async () => {
		if (!currentActiveDocument || !currentActiveDocument.id) {
			ZMessage.error("No active document");
			return;
		}
		dispatch(DocumentsActionCreators.showDocumentsLoader());
		dispatch(DocumentsActionCreators.downloadDocumentModalOpened());
		try {
			const { url } = await downloadDocumentUsingURL(
				currentActiveDocument.id
			);
			window.open(url);
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(DocumentsActionCreators.hideDocumentsLoader());
			dispatch(DocumentsActionCreators.downloadDocumentModalClosed());
		}
	};

	const downloadDocumentProductList = async () => {
		if (!currentActiveDocument?.revisions?.[0].revision_id) {
			ZMessage.error(DefaultErrorMessage);
			return;
		}
		dispatch(DocumentsActionCreators.downloadDocumentModalOpened());
		try {
			const downloadedFileName =
				currentActiveDocument.name.length > 0
					? currentActiveDocument.name
					: "Document Product List";
			const documentId = currentActiveDocument.id;
			const revisionId = currentActiveDocument.revisions[0].revision_id;

			const downloadDocumentProductListRequest: DownloadDocumentProductListRequest =
				{
					downloadedFileName,
					documentId,
					revisionId
				};

			await dispatch(
				DocumentsActionCreators.downloadDocumentProductList(
					downloadDocumentProductListRequest
				)
			);
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(DocumentsActionCreators.downloadDocumentModalClosed());
		}
	};

	const fetchDocumentPdfUrl = async (forSigning?: boolean) => {
		dispatch(SettingsActionCreators.fetchingPdf());
		try {
			if (!currentActiveDocument) {
				ZMessage.error("No active document");
				return;
			} else if (currentActiveDocument.is_deleted) {
				return;
			}

			const response = await downloadWonDocument(
				currentActiveDocument.id,
				forSigning
			);

			return response.url;
		} catch (error) {
			console.error(error);
			if (
				error?.code === 504 &&
				error?.displayMessage?.includes("PDF TIMEOUT")
			) {
				ZMessage.error(
					"Document embedding not successful. Please ensure the total number of pages in the document does not exceed 25."
				);
			}
		} finally {
			dispatch(SettingsActionCreators.resetFetchingPdf());
		}

		return undefined;
	};

	const DownloadDocument = (
		<DownloadDocumentModal visible={downloadDocumentModalOpen} />
	);

	return {
		downloadDocument,
		fetchDocumentPdfUrl,
		downloadDocumentProductList,
		DownloadDocument
	};
};

export default useDownload;
